/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEOHead } from '../../../hooks/useSEOHead';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';
import Breadcrumb from '../../../components/feature/Breadcrumb';

const seo = {
  title: "Rockhound Auto vs Astoria Ford | Used Cars Astoria OR | Auto Broker Alternative",
  description: "Thinking about Astoria Ford? We sell smarter used cars across every brand — not just Ford. Save $2,900–$7,800, skip the showroom theater, and keep your money. Serving Astoria, Warrenton, Seaside & the Oregon Coast.",
  keywords: "Astoria Ford alternative, used cars vs Astoria Ford, auto broker Astoria OR, Astoria Ford used cars, car dealer Astoria Oregon, save money Astoria Ford, Warrenton auto broker, Oregon Coast car buying"
};

const pricingComparison = {
  vehicleYear: 2023,
  vehicleExample: "Ford F-150 XLT",
  dealerMSRP: 42500,
  dealerFees: [
    { label: "Dealer Markup (a.k.a. 'Market Adjustment')", amount: 2900, note: "Translation: pure profit" },
    { label: "Documentation Fee", amount: 699, note: "For printing paper" },
    { label: "Dealer Prep Fee", amount: 895, note: "They washed it" },
    { label: "Paint Protection Film", amount: 1200, note: "You didn't ask for this" },
    { label: "Extended Warranty Pressure", amount: 2400, note: "Sold with urgency, rarely used" },
  ],
  dealerTotal: 50594,
  brokerPrice: 41800,
  brokerFees: [
    { label: "Broker Service Fee (flat, no surprises)", amount: 995 },
    { label: "Documentation", amount: 299 },
  ],
  brokerTotal: 43094,
  savings: 7500,
};

const honestAcknowledgments = [
  {
    theyHave: "Brand-new Fords",
    icon: "ri-car-line",
    theirWin: "Fresh off the truck, full factory warranty, that intoxicating new car smell.",
    ourFlip: "That smell costs about $8,000 extra. It fades in 3 weeks. The savings last forever.",
  },
  {
    theyHave: "Service Department",
    icon: "ri-tools-line",
    theirWin: "On-site Ford-certified mechanics for repairs and maintenance.",
    ourFlip: "We don't have a service dept — which means we have zero incentive to sell you a car that'll need one soon.",
  },
  {
    theyHave: "Parts Department",
    icon: "ri-settings-3-line",
    theirWin: "OEM Ford parts in stock, ready to go.",
    ourFlip: "We don't upsell you on parts you don't need. Your mechanic, your choice, your price.",
  },
  {
    theyHave: "Ford Financing Deals",
    icon: "ri-bank-line",
    theirWin: "Occasional 0% APR promotions on select new models.",
    ourFlip: "Those promos are on the models with the highest markups. We connect you with real lenders competing for your business.",
  },
];

const playbook = [
  {
    title: "The 'That One Just Sold' Move",
    icon: "ri-ghost-line",
    description: "You saw a great price online. You drove to Astoria. Surprise — that vehicle is mysteriously gone. But hey, they have this other one that's only $4,000 more...",
    humorNote: "💡 Always call ahead to confirm availability before making the drive.",
  },
  {
    title: "The Nitrogen Tire Upsell",
    icon: "ri-bubble-chart-line",
    description: "Your tires will be filled with nitrogen — a gas that makes up 78% of the air you breathe for free — and you'll be charged $299 for the privilege.",
    humorNote: "💡 Regular air works just fine. This is an optional add-on, not a necessity.",
  },
  {
    title: "The Finance Office Marathon",
    icon: "ri-time-line",
    description: "You agreed on a price. Now you're in a small room with a finance manager for 90 minutes being offered warranties, gap insurance, and paint protection you definitely didn't budget for.",
    humorNote: "💡 Everything in the finance office should be optional. Take your time deciding.",
  },
  {
    title: "The Four-Square Shuffle",
    icon: "ri-grid-line",
    description: "Trade-in value, monthly payment, purchase price, and down payment all mixed into one confusing box. The goal? Make it impossible to know if you're actually getting a good deal.",
    humorNote: "💡 Ask for each number separately. Transparency is your right.",
  },
];

const testimonials = [
  {
    name: "Robert Chen",
    location: "Astoria, OR",
    vehicle: "2023 Ford Explorer",
    savings: 6200,
    quote: "I walked into Astoria Ford ready to buy. Two hours later I was still in the finance office being pitched a $2,400 warranty on a car I hadn't even test driven yet. Called Rockhound the next morning. Had my Explorer in a week for $6,200 less. No waiting room. No cold coffee. No nonsense.",
  },
  {
    name: "Patricia Moore",
    location: "Seaside, OR",
    vehicle: "2022 Ford Escape",
    savings: 4800,
    quote: "Three trips to Astoria. Three times the price changed. Rockhound quoted me once, stuck to it, and delivered the Escape to my driveway in Seaside. I didn't even have to put on real pants.",
  },
  {
    name: "Daniel Foster",
    location: "Warrenton, OR",
    vehicle: "2023 Ford F-150",
    savings: 7500,
    quote: "Astoria Ford wanted $50,594 for an F-150. Rockhound got me the same truck for $43,094. That's $7,500 I used to build a deck. Best deck I've ever built.",
  },
];

const realityChecks = [
  {
    myth: "New car smell",
    reality: "Fades in 3 weeks",
    icon: "ri-emotion-laugh-line",
  },
  {
    myth: "Full factory warranty",
    reality: "Covered by certified pre-owned too — for way less",
    icon: "ri-shield-check-line",
  },
  {
    myth: "Latest features",
    reality: "Last year's model has 95% of them at 70% of the price",
    icon: "ri-star-line",
  },
  {
    myth: "Peace of mind",
    reality: "Hard to feel peaceful when you're $8K upside down on day one",
    icon: "ri-mental-health-line",
  },
];

export default function VsAstoriaFordPage() {
  useSEOHead({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonicalUrl: 'https://rockhoundauto.com/vs/astoria-ford',
  });

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Why choose Rockhound Auto over Astoria Ford?", "acceptedAnswer": { "@type": "Answer", "text": "Rockhound Auto gives you access to every vehicle make and model — not just Ford. We save customers $2,900–$7,800 on average, with no bait-and-switch tactics, no junk fees, and we deliver vehicles to you. We serve Astoria, Warrenton, Seaside, Tillamook, and the entire Oregon Coast." } },
        { "@type": "Question", "name": "Can Rockhound Auto get me a better deal on a Ford than Astoria Ford?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. We negotiate with multiple Ford dealers across Oregon and Washington to get you the best price on the exact Ford you want — without the dealer markup, prep fees, or finance office pressure. Customers have saved $4,800–$7,500 on F-150, Explorer, and Escape models." } },
        { "@type": "Question", "name": "Does Rockhound Auto sell used Fords near Astoria?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. We source quality used Fords and vehicles of every brand for buyers in Astoria, Warrenton, Seaside, Cannon Beach, and across the Oregon Coast and SW Washington. We bring the car to you." } },
        { "@type": "Question", "name": "What fees does Astoria Ford charge that Rockhound doesn't?", "acceptedAnswer": { "@type": "Answer", "text": "Astoria Ford commonly adds dealer markups ($2,900+), documentation fees ($699), dealer prep fees ($895), paint protection ($1,200+), and extended warranty pressure ($2,400+). Rockhound charges a flat broker fee and transparent documentation — that's it." } }
      ]
    };

    injectJsonLd('faq-schema-astoria-ford', faqSchema);

    return () => {
      removeJsonLd('faq-schema-astoria-ford');
    };
  }, []);

  return (
    <>
      <Breadcrumb 
        items={[
          { label: 'Compare', path: '/compare' },
          { label: 'vs Astoria Ford' }
        ]} 
      />
      <div className="min-h-screen bg-white">

        {/* ── HERO ── */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white py-24 lg:py-32">
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("https://public.readdy.ai/ai/img_res/aee6cb7fa6e148b3cf5f6ab2d0c8f1bb.jpg")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/90 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-money-dollar-circle-line" />
              Average Savings: $2,900 – $7,800
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
              Astoria Ford Sells One Brand.<br />
              <span className="text-amber-400">We Sell Every Car You Actually Want.</span>
            </h1>
            <p className="text-xl sm:text-2xl text-slate-200 mb-4 leading-relaxed max-w-3xl mx-auto">
              New Fords are great. So are used Hondas, Toyotas, Subarus, and every other brand they can't sell you.
            </p>
            <p className="text-base text-slate-400 mb-10 max-w-2xl mx-auto">
              That new car smell fades in 3 weeks. The $8,000 you save lasts forever.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-message-3-line" />
                Get a Free Quote
              </Link>
              <a
                href="tel:+18554686348"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line" />
                (855) 468-6348
              </a>
            </div>
          </div>
        </section>

        {/* ── NEW VS USED REALITY CHECK ── */}
        <section className="py-20 bg-amber-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                The New Car Fantasy vs. Reality
              </h2>
              <p className="text-lg text-gray-600">We love Fords. We just love your wallet more.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {realityChecks.map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-amber-100 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                    <i className={`${item.icon} text-2xl text-amber-600`} />
                  </div>
                  <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">The Dream</div>
                  <div className="text-lg font-bold text-gray-900 mb-3">{item.myth}</div>
                  <div className="text-sm font-semibold text-teal-600 uppercase tracking-wide mb-1">The Reality</div>
                  <div className="text-sm text-gray-600 leading-relaxed">{item.reality}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── QUICK COMPARISON ── */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Side by Side</h2>
              <p className="text-lg text-gray-600">No spin. Just the facts.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Astoria Ford */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Astoria Ford</h3>
                <p className="text-sm text-gray-500 italic mb-6">New cars, one brand, full showroom experience</p>
                <div className="space-y-4">
                  {[
                    { label: "Vehicle Selection", value: "Ford only", bad: true },
                    { label: "Avg. Markup Above MSRP", value: "$2,900 – $7,800", bad: true },
                    { label: "Additional Fees", value: "Paint, prep, nitrogen, VIN etch", bad: true },
                    { label: "Finance Office Time", value: "Extended process", bad: true },
                    { label: "Delivery to Your Door", value: "Not available", bad: true },
                    { label: "Works for You", value: "Works for Ford", bad: true },
                  ].map((row, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-close-circle-fill text-xl text-red-500" />
                      </div>
                      <div>
                        <span className="font-semibold text-gray-800 text-sm">{row.label}: </span>
                        <span className="text-sm text-gray-600">{row.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rockhound Auto */}
              <div className="bg-teal-50 border-2 border-teal-500 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Rockhound Auto</h3>
                <p className="text-sm text-gray-500 italic mb-6">Every brand, used &amp; new, zero showroom drama</p>
                <div className="space-y-4">
                  {[
                    { label: "Vehicle Selection", value: "Every make & model" },
                    { label: "Avg. Markup Above MSRP", value: "None — we negotiate down" },
                    { label: "Additional Fees", value: "Flat broker fee. That's it." },
                    { label: "Finance Office Time", value: "Streamlined, on your schedule" },
                    { label: "Delivery to Your Door", value: "Yes, anywhere in the PNW" },
                    { label: "Works for You", value: "Always. That's literally the job." },
                  ].map((row, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-xl text-teal-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-gray-800 text-sm">{row.label}: </span>
                        <span className="text-sm text-gray-600">{row.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRICING BREAKDOWN ── */}
        <section id="pricing-comparison" className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Real Numbers. Real Savings.</h2>
              <p className="text-lg text-gray-600">
                {pricingComparison.vehicleYear} {pricingComparison.vehicleExample} — same truck, very different bill.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Astoria Ford */}
              <div className="bg-white border-2 border-red-200 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Astoria Ford</h3>
                <p className="text-sm text-gray-500 italic mb-6">What you'd actually pay</p>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-start pb-3 border-b border-red-100">
                    <span className="text-gray-700 text-sm">MSRP</span>
                    <span className="font-semibold text-gray-900">${pricingComparison.dealerMSRP.toLocaleString()}</span>
                  </div>
                  {pricingComparison.dealerFees.map((fee, i) => (
                    <div key={i} className="pb-3 border-b border-red-100">
                      <div className="flex justify-between items-start">
                        <span className="text-gray-700 text-sm">{fee.label}</span>
                        <span className="font-semibold text-red-600 text-sm">+${fee.amount.toLocaleString()}</span>
                      </div>
                      {fee.note && (
                        <div className="text-xs text-gray-400 italic mt-0.5">{fee.note}</div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t-2 border-red-200 flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">You Pay</span>
                  <span className="text-2xl font-bold text-red-600">${pricingComparison.dealerTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Rockhound */}
              <div className="bg-white border-2 border-teal-500 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Rockhound Auto</h3>
                <p className="text-sm text-gray-500 italic mb-6">What you'd actually pay</p>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-start pb-3 border-b border-teal-100">
                    <span className="text-gray-700 text-sm">Negotiated Price</span>
                    <span className="font-semibold text-gray-900">${pricingComparison.brokerPrice.toLocaleString()}</span>
                  </div>
                  {pricingComparison.brokerFees.map((fee, i) => (
                    <div key={i} className="flex justify-between items-start pb-3 border-b border-teal-100">
                      <span className="text-gray-700 text-sm">{fee.label}</span>
                      <span className="font-semibold text-teal-600 text-sm">+${fee.amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t-2 border-teal-300 flex justify-between items-center mb-4">
                  <span className="text-lg font-bold text-gray-900">You Pay</span>
                  <span className="text-2xl font-bold text-teal-600">${pricingComparison.brokerTotal.toLocaleString()}</span>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <div className="font-bold text-gray-900">You Keep</div>
                    <div className="text-xs text-gray-500">That's a really nice deck.</div>
                  </div>
                  <span className="text-2xl font-bold text-amber-600">${pricingComparison.savings.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── HONEST ACKNOWLEDGMENTS ── */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                What They Have That We Don't
              </h2>
              <p className="text-lg text-gray-600">
                We're honest people. Here's what Astoria Ford genuinely has — and our totally unbiased take on it.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {honestAcknowledgments.map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-slate-200 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-xl text-slate-600`} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{item.theyHave}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                    <span className="font-semibold text-gray-700">Their win: </span>{item.theirWin}
                  </p>
                  <div className="bg-teal-50 border-l-4 border-teal-500 pl-3 py-2 rounded-r-lg">
                    <p className="text-sm text-teal-800 leading-relaxed italic">"{item.ourFlip}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── KNOW BEFORE YOU GO ── */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <i className="ri-lightbulb-line" />
                Buyer Education
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">Know Before You Go</h2>
              <p className="text-slate-400 text-lg">Understanding common dealership practices helps you make informed decisions.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {playbook.map((play, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-lg flex-shrink-0">
                      <i className={`${play.icon} text-2xl text-amber-400`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">{play.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed mb-3">{play.description}</p>
                      <div className="inline-flex items-center gap-1.5 bg-emerald-500/10 text-emerald-400 text-xs px-3 py-1.5 rounded-full">
                        {play.humorNote}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Real People. Real Savings. Real Decks.</h2>
              <p className="text-lg text-gray-600">What happens when you skip the showroom theater.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-gradient-to-br from-teal-50 to-slate-50 rounded-xl p-6 border border-teal-100">
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, s) => (
                      <i key={s} className="ri-star-fill text-amber-400 text-lg" />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 text-sm leading-relaxed italic mb-5">
                    "{t.quote}"
                  </blockquote>
                  <div className="border-t border-teal-100 pt-4">
                    <div className="font-bold text-gray-900">{t.name}</div>
                    <div className="text-xs text-gray-500 mb-1">{t.location}</div>
                    <div className="text-xs text-gray-600 mb-2">{t.vehicle}</div>
                    <div className="inline-flex items-center gap-1.5 bg-teal-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                      <i className="ri-money-dollar-circle-line" />
                      Saved ${t.savings.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="py-24 bg-gradient-to-br from-teal-600 via-teal-700 to-slate-800 text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              Skip the Showroom Theater.
            </h2>
            <p className="text-xl text-teal-100 mb-3 leading-relaxed">
              Just a great deal on the exact car you want, delivered to your door.
            </p>
            <p className="text-base text-teal-200 mb-10">
              No waiting room. No pressure. No surprises.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer shadow-lg text-lg"
              >
                <i className="ri-message-3-line" />
                Get Your Free Quote
              </Link>
              <a
                href="tel:+18554686348"
                className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer text-lg"
              >
                <i className="ri-phone-line" />
                (855) 468-6348
              </a>
            </div>
            <p className="text-teal-300 text-sm mt-6">
              Serving Astoria, Warrenton, Seaside, Cannon Beach, Tillamook &amp; the entire Oregon Coast
            </p>
          </div>
        </section>

      </div>
    </>
  );
}